import * as XLSX from 'xlsx';
import type { Category, InventoryItem, Supplier } from '@/types/schemas';
import {
  assignSku,
  createInventoryItem,
} from '../inventoryQueries';
import { createCategory, deriveCodePrefix, findOrCreateBrand, isCodePrefixAvailable } from '../setupQueries';
import { createSupplier } from '../supplierQueries';

export type ImportRowStatus = 'valid' | 'warning' | 'error';

export interface ImportReviewRow {
  rowIndex: number;
  category: string;
  brand: string;
  productName: string;
  costPrice: number;
  sellingPrice: number;
  unit: string;
  initialStock: number;
  lowStockThreshold: number;
  supplier: string;
  status: ImportRowStatus;
  messages: string[];
  brandId?: string;
  categoryId?: string;
  supplierId?: string;
}

const EXPECTED_HEADERS = [
  'Category',
  'Brand',
  'Product Name',
  'Cost Price',
  'Selling Price',
  'Unit',
  'Initial Stock',
  'Low Stock Threshold',
  'Supplier',
] as const;

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, ' ');
}

function getCell(row: Record<string, string>, header: string): string {
  return row[normalizeHeader(header)] ?? '';
}

export async function parseExcelFile(buffer: ArrayBuffer): Promise<Record<string, string>[]> {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error('Excel file is empty');
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1 });
  const [headerRow, ...dataRows] = rows;
  if (!headerRow?.length) throw new Error('Missing header row');

  const headers = headerRow.map((cell) => normalizeHeader(String(cell ?? '')));
  const missing = EXPECTED_HEADERS.filter((h) => !headers.includes(normalizeHeader(h)));
  if (missing.length > 0) throw new Error(`Missing columns: ${missing.join(', ')}`);

  const normalizedRows = dataRows.map((row) => {
    const normalized: Record<string, string> = {};
    headers.forEach((header, index) => {
      if (header) normalized[header] = String((row as unknown[])[index] ?? '').trim();
    });
    return normalized;
  });

  return normalizedRows.filter((row) =>
    EXPECTED_HEADERS.some((h) => getCell(row, h).length > 0),
  );
}

export function buildImportReviewRows(
  rows: Record<string, string>[],
  categories: Category[],
  suppliers: Supplier[],
  options: { canViewCost: boolean },
): ImportReviewRow[] {
  return rows.map((row, index) => {
    const messages: string[] = [];
    let status: ImportRowStatus = 'valid';

    const categoryName = getCell(row, 'Category');
    const brand = getCell(row, 'Brand');
    const productName = getCell(row, 'Product Name');
    const sellingPrice = Number(getCell(row, 'Selling Price').replace(/,/g, '') || 0);
    const costPriceRaw = getCell(row, 'Cost Price');
    const costPrice = options.canViewCost ? Number(costPriceRaw.replace(/,/g, '') || 0) : 0;
    const unit = getCell(row, 'Unit') || 'piece';
    const initialStock = Number(getCell(row, 'Initial Stock').replace(/,/g, '') || 0);
    const threshold = Number(getCell(row, 'Low Stock Threshold').replace(/,/g, '') || 5);
    const supplierName = getCell(row, 'Supplier');

    if (!categoryName) messages.push('Category is required');
    if (!brand) messages.push('Brand is required');
    if (!productName) messages.push('Product name is required');
    if (sellingPrice <= 0) messages.push('Selling price must be positive');
    if (options.canViewCost && costPrice > sellingPrice) messages.push('Cost exceeds selling price');

    const matchedCategory = categories.find(
      (c) => c.name.toLowerCase() === categoryName.toLowerCase(),
    );
    if (!matchedCategory && categoryName) {
      messages.push(`Category "${categoryName}" will be created`);
      status = 'warning';
    }

    let supplierId: string | undefined;
    if (supplierName) {
      const matched = suppliers.find((s) => s.name.toLowerCase() === supplierName.toLowerCase());
      if (matched) supplierId = matched.id;
      else {
        messages.push(`Supplier "${supplierName}" will be created`);
        if (status === 'valid') status = 'warning';
      }
    }

    if (messages.some((m) => !m.includes('will be created'))) status = 'error';

    return {
      rowIndex: index + 2,
      category: categoryName,
      brand,
      productName: productName || `${brand} ${categoryName}`.trim(),
      costPrice,
      sellingPrice,
      unit,
      initialStock: Number.isFinite(initialStock) ? initialStock : 0,
      lowStockThreshold: Number.isFinite(threshold) ? threshold : 5,
      supplier: supplierName,
      status,
      messages,
      categoryId: matchedCategory?.id,
      supplierId,
    };
  });
}

export async function importProductRows(
  rows: ImportReviewRow[],
  options: {
    storeId: string;
    userId: string;
    canViewCost: boolean;
    categories: Category[];
    suppliers: Supplier[];
  },
): Promise<{ imported: number; failed: number }> {
  let imported = 0;
  let failed = 0;
  const categoryCache = new Map<string, Category>();
  const supplierCache = new Map<string, Supplier>();

  for (const row of rows) {
    if (row.status === 'error') {
      failed += 1;
      continue;
    }

    try {
      let categoryId = row.categoryId;
      if (!categoryId) {
        const key = row.category.toLowerCase();
        let cat = categoryCache.get(key);
        if (!cat) {
          let prefix = deriveCodePrefix(row.category);
          for (let i = 0; i < 10; i++) {
            if (await isCodePrefixAvailable(prefix)) break;
            prefix = `${deriveCodePrefix(row.category).slice(0, 3)}${i + 1}`.slice(0, 5);
          }
          const id = await createCategory({
            name: row.category.trim(),
            codePrefix: prefix,
            attributeSchema: [],
            lowStockThresholdDefault: row.lowStockThreshold,
            hasExpiry: false,
            isActive: true,
          });
          cat = {
            id,
            name: row.category.trim(),
            codePrefix: prefix,
            attributeSchema: [],
            lowStockThresholdDefault: row.lowStockThreshold,
            hasExpiry: false,
            isActive: true,
          };
          categoryCache.set(key, cat);
          options.categories.push(cat);
        }
        categoryId = cat.id;
      }

      let supplierId = row.supplierId;
      if (row.supplier && !supplierId) {
        const key = row.supplier.toLowerCase();
        let sup = supplierCache.get(key);
        if (!sup) {
          const id = await createSupplier({
            name: row.supplier.trim(),
            contactPhone: '',
            notes: '',
            totalAmount: 0,
            advanceAmount: 0,
            isActive: true,
          });
          sup = {
            id,
            name: row.supplier.trim(),
            contactPhone: '',
            notes: '',
            totalAmount: 0,
            advanceAmount: 0,
            isActive: true,
          };
          supplierCache.set(key, sup);
        }
        supplierId = sup.id;
      }

      const category = options.categories.find((c) => c.id === categoryId);
      const brandRecord = await findOrCreateBrand(row.brand, categoryId!);
      const sku = await assignSku(category?.codePrefix ?? deriveCodePrefix(row.category));

      const item: Omit<InventoryItem, 'id'> = {
        itemName: row.productName,
        categoryId: categoryId!,
        category: row.category,
        brandId: brandRecord.id,
        brand: brandRecord.name,
        subBrand: '',
        variant: '',
        sku,
        unit: row.unit,
        length: '',
        width: '',
        thickness: '',
        attributeValues: {},
        purchasePrice: options.canViewCost ? row.costPrice : 0,
        sellingPrice: row.sellingPrice,
        quantity: 0,
        lowStockThreshold: row.lowStockThreshold,
        expiryDate: '',
        imageUrl: '',
        supplierId: supplierId ?? '',
        notes: '',
        source: 'excel_import',
        isActive: true,
      };

      await createInventoryItem(item, options.userId, {
        storeId: options.storeId,
        initialStock: row.initialStock,
      });
      imported += 1;
    } catch {
      failed += 1;
    }
  }

  return { imported, failed };
}

export function downloadImportTemplate() {
  const csv = `${EXPECTED_HEADERS.map((h) => `"${h}"`).join(',')}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'inventory-import-template.csv';
  link.click();
  URL.revokeObjectURL(url);
}
